/** Pipelines, remotely: definitions and recent runs per source; run, re-run and cancel. Approvals stay in the app. */

import { CircleCheck, CircleXmark, Play, Rocket } from '@gravity-ui/icons';
import { useState } from 'react';

import { Button, ChoiceSelect, Empty, ListBlock, ListGroup, ListRow, Stat, StatusMark } from '../../components/ui.tsx';
import { RowMenu } from '../../components/ui/RowMenu.tsx';
import { timeAgo } from '../../hooks.ts';
import type { PipelinesState, Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

function runState(r: { status: string; conclusion?: string }): { state: 'working' | 'low' | 'error' | 'idle'; label: string } {
  const words = (s: string) => {
    const t = s.replace('_', ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
    return `${t[0]!.toUpperCase()}${t.slice(1)}`;
  };
  if (r.status === 'in_progress' || r.status === 'queued' || r.status === 'running' || r.status === 'inProgress') return { state: 'working', label: words(r.status) };
  if (r.conclusion === 'success' || r.conclusion === 'succeeded') return { state: 'low', label: 'Passed' };
  if (r.conclusion === 'failure' || r.conclusion === 'failed' || r.conclusion === 'cancelled' || r.conclusion === 'canceled') return { state: 'error', label: words(r.conclusion) };
  return { state: 'idle', label: words(r.conclusion ?? r.status) };
}

export function PipelinesView({ w }: { w: Workbench }) {
  const state = w.useView<PipelinesState>('pipelines.list', undefined, [], 30_000);
  const [branch, setBranch] = useState<string>();
  const sources = state.data?.sources ?? [];
  const chosen = branch ?? state.data?.branch ?? state.data?.branches[0] ?? 'main';
  const runs = sources.flatMap((s) => s.runs.slice(0, 12).map(runState));
  const running = runs.filter((r) => r.state === 'working').length;
  const failed = runs.filter((r) => r.state === 'error').length;
  const pipelines = sources.reduce((n, s) => n + s.pipelines.length, 0);
  return (
    <RemoteView>
      {sources.length > 0 && (
        <div className="art-stats">
          <Stat tone="blue" icon={<Play />} label="Running" value={running} detail={running ? 'watching until they finish' : 'nothing running'} />
          <Stat tone="rose" icon={<CircleXmark />} label="Failed" value={failed} detail={failed ? 'of the recent runs' : 'no recent run failed'} needsYou={failed > 0} />
          <Stat tone="green" icon={<CircleCheck />} label="Pipelines" value={pipelines} detail={`in ${sources.length} ${sources.length === 1 ? 'source' : 'sources'}`} />
        </div>
      )}
      {sources.length === 0 && <Empty tone="teal" icon={<Rocket />} title="No pipeline sources" body={state.data?.note ?? 'The workbench finds GitHub Actions and Azure Pipelines from the root’s remotes once it is signed in.'} />}
      {sources.map((s) => (
        <ListGroup
          key={s.id}
          title={s.label}
          count={s.pipelines.length || undefined}
          description={s.error ?? (s.signedIn ? 'Its pipelines, and the recent runs under them. Approvals stay in the app.' : 'Not signed in.')}
          actions={<ChoiceSelect label="Branch to run on" value={chosen} options={Object.fromEntries((state.data?.branches ?? [chosen]).map((b) => [b, b]))} onChange={setBranch} />}
        >
          {s.pipelines.map((p) => (
            <ListRow
              key={p.id}
              icon={<Rocket />}
              title={p.name}
              description="Pipeline"
              compactControl
              control={
                <Button size="sm" variant="secondary" onPress={() => void w.act('Run', () => w.call('pipelines.act', { source: s.id, action: { kind: 'run', pipelineId: p.id, branch: chosen } }), state.reload)}>
                  Run on {chosen}
                </Button>
              }
            />
          ))}
          {s.runs.length ? null : <ListBlock className="text-sm text-muted">No recent runs on this source.</ListBlock>}
          {s.runs.slice(0, 12).map((r) => {
            const st = runState(r);
            return (
              <ListRow
                key={r.id}
                icon={<Play />}
                title={
                  r.webUrl ? (
                    <a href={r.webUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                      {r.title ?? r.name ?? r.id}
                    </a>
                  ) : (
                    (r.title ?? r.name ?? r.id)
                  )
                }
                description={`Run${r.branch ? ` on ${r.branch}` : ''}${r.updatedAt || r.startedAt ? ` · ${timeAgo(String(r.updatedAt ?? r.startedAt))}` : ''}`}
                status={<StatusMark state={st.state} label={st.label} />}
                compactControl
                control={
                  <RowMenu
                    label={`More for ${r.title ?? r.name ?? r.id}`}
                    items={[st.state === 'working' ? { id: 'cancel', label: 'Cancel the run', danger: true } : { id: 'rerun', label: st.state === 'error' ? 'Re-run failed jobs' : 'Re-run' }]}
                    onAction={(k) =>
                      void (k === 'cancel'
                        ? w.act('Cancel run', () => w.call('pipelines.act', { source: s.id, action: { kind: 'cancel', runId: r.id } }), state.reload)
                        : w.act('Re-run', () => w.call('pipelines.act', { source: s.id, action: { kind: 'rerun', runId: r.id, failedOnly: st.state === 'error' } }), state.reload))
                    }
                  />
                }
              />
            );
          })}
        </ListGroup>
      ))}
    </RemoteView>
  );
}
